import React, { useRef, useState, useEffect } from "react";
import FileUploadIcon from "../../assets/icons/FileUploadIcon.svg";
import { FaFilePdf } from "react-icons/fa";
import styles from "./FileUpload.module.css";

const FileUpload = ({
  label = "Upload File",
  name,
  register,
  allowedTypes = ["pdf"],
  maxSizeMB = 5,
  onFileSelect,
  errorMessage,
  previewUrl, // API Preview URL
  disabled,
}) => {
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null); // new uploaded file
  const [error, setError] = useState("");
  const uploadRef = useRef(null);

  const allowedMimeMap = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
  };

  const { onChange: rhfOnChange, ref: rhfRef } = register
    ? register(name)
    : { onChange: () => {}, ref: () => {} };

  useEffect(() => {
    if (previewUrl) {
      // Tell RHF that this field ALREADY has a value
      rhfOnChange({
        target: {
          name,
          value: previewUrl,
          files: [],
        },
      });
    }
  }, [previewUrl, rhfOnChange]);

  const validateFile = (file) => {
    setError("");

    const ext = file.name.split(".").pop().toLowerCase();
    const validExt = allowedTypes.includes(ext);
    const validMime = allowedMimeMap[ext]
      ? file.type === allowedMimeMap[ext]
      : true;

    if (!validExt || !validMime) {
      setError(`Allowed formats: ${allowedTypes.join("/").toUpperCase()}`);
      return false;
    }

    if (file.size / 1024 / 1024 > maxSizeMB) {
      setError(`Max size allowed: ${maxSizeMB} MB`);
      return false;
    }

    return true;
  };

  const handleFile = (file) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    setFileName(file.name);

    onFileSelect && onFileSelect(file);
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    handleFile(file);
    rhfOnChange(e); // send file to React Hook Form
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;

    handleFile(file);

    const dt = new DataTransfer();
    dt.items.add(file);
    const input = uploadRef.current;
    input.files = dt.files;

    rhfOnChange({ target: input });
  };
  const isPdf = (file) => {
    if (!file) return false;
    return file.type === "application/pdf";
  };

  const isPdfUrl = (url) => {
    if (!url) return false;
    return url.toLowerCase().endsWith(".pdf");
  };

  return (
    <div>
      <fieldset
        className={`${styles.fieldsetBox}
         ${disabled ? styles.disabled : ""}
        ${
          !fileName &&
          !selectedFile &&
          !previewUrl &&
          !error &&
          errorMessage &&
          styles.fieldsetBoxAlert
        }`}
        onClick={() => uploadRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <legend className={styles.legend}>{label}</legend>

        <div className={styles.uploadArea}>
          {!disabled && (
            <img
              src={FileUploadIcon}
              alt="upload"
              className={styles.uploadIcon}
            />
          )}
          {fileName ? (
            <span className={styles.fileName}>{fileName}</span>
          ) : previewUrl ? (
            <span className={styles.fileName}> </span>
          ) : (
            <span className={styles.fileText}>
              File Upload ({allowedTypes.map((t) => t.toUpperCase()).join("/")})
            </span>
          )}
        </div>

        <input
          type="file"
          name={name}
          accept={allowedTypes.map((t) => "." + t).join(",")}
          hidden
          ref={(el) => {
            uploadRef.current = el;
            rhfRef(el);
          }}
          onChange={handleChange}
          disabled={disabled}
        />

        {/* RHF required error */}
        {!fileName &&
          !selectedFile &&
          !previewUrl &&
          !error &&
          errorMessage && <p className={styles.error}>{errorMessage}</p>}

        {/* Local validation error */}
        {error && <p className={styles.error}>{error}</p>}

        {/* Preview from API if no new file selected */}
        {previewUrl && !selectedFile && (
          <>
            {isPdfUrl(previewUrl) ? (
              <div className={styles.pdfPreview}>
                {previewUrl && <span>{previewUrl.split("/").pop()}</span>}

                <FaFilePdf className={styles.pdfIcon} />
              </div>
            ) : (
              <div className={styles.imagePreview}>
                <img src={previewUrl} alt="Preview" />
              </div>
            )}
          </>
        )}
        {/* New uploaded file preview */}
        {selectedFile && (
          <div className={styles.imagePreview}>
            {isPdf(selectedFile) ? (
              <FaFilePdf className={styles.pdfIcon} />
            ) : (
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Uploaded Preview"
                style={{ objectFit: "contain", marginTop: "10px" }}
              />
            )}
          </div>
        )}
      </fieldset>
    </div>
  );
};

export default FileUpload;
